// @vendors
import React, { Fragment, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions } from 'react-redux-form';

// @ commons
import { FormattedText, formatText } from 'core/i18n';

//@utilities
import { RELIABLE, PAGE_PRODUCT, recordEvent, recordEventError, recordEventCustomOnly } from 'utilities/uTagging';
import { getUserProfileData } from 'utilities/userProfile';

// @ components
import ConditionalToggling from 'utilities/toggling/ConditionalToggling/ConditionalToggling';
import MessageBox from 'components/commons/MessageBox/MessageBox';
import OtpTransferMessages from 'componentsV2/OTPTransferCodeBox/OTPTransferMessages/OTPTransferMessages.jsx';

//@constants
import { IBAN_TRANSFER_OTP_RETRY_OTP, IBAN_TRANSFER_OTP_VALIDATE } from 'constants/identityMethod';
import { TRANSFER_OTP_PATH, TRANSFER_OTP_PATH_TAG } from 'constants/uTagConstants/paths';

// @actions
import ibanActions from 'actions/ibanActions';
import preLoaderActions from 'actions/preLoaderActions';
import { SplitInput } from '../SplitInput/SplitInput';
import DOCUMENTATION_PRODUCT_NAME from '../../utilities/uTagging';
import { useSplitInput } from '../SplitInput/useSplitInput';
import {
    OtpTransferCodeBoxActions,
    OtpTransferCodeBoxInitialState,
    OtpTransferCodeBoxReducer,
} from './OTPTransferCodeBoxReducer';
import { ONLY_ALPHANUMERIC_REGEX } from '../../constants/regex';

const OTP_CHARACTERS_LENGTH = 6;

const OtpTransferCodeBox = ({
    formActions,
    onHandleDisableNextButton,
    userProfile,
    ibanActions,
    preLoaderActions,
    onHandleSetOtpTransferData,
    showAlternativeOption,
    hideAlternativeOptionModal,
    showHelpModal,
    handleOnFocusInput,
    retryTransfer,
    displayCodeNotFound,
}) => {
    const [
        {
            errorMessage,
            incorrectOtp,
            incorrectOtpRetry,
            expiredOtpRetry,
            incorrectOtpWithoutRetry,
            expiredOtpWithoutRetry,
            isOtpValidated,
            showOtpButton,
            isClickedRetryOTPChallenge,
        },
        dispatch,
    ] = useReducer(OtpTransferCodeBoxReducer, OtpTransferCodeBoxInitialState);

    useEffect(() => {
        formActions.change('userProfile.otpCode', '');
        formActions.resetValidity('userProfile.otpCode');
    }, []);
    const {
        value: otpValue,
        isCompleted: isOtpCompleted,
        onChange: handleOnChangeSplitInput,
        resetValue: resetOtpValue,
    } = useSplitInput();

    const finishStep = () => {
        onHandleDisableNextButton(false);
    };

    const handleRetryOTPClick = async () => {
        preLoaderActions.showPreLoader();
        recordTransferOtpEvent({
            path: TRANSFER_OTP_PATH,
            actionEvent: TRANSFER_OTP_PATH_TAG,
            tagEvent: 'reintentar',
        });

        const response = await ibanActions.ibanTransferOTPCall(
            userProfile.accessToken,
            'POST',
            IBAN_TRANSFER_OTP_RETRY_OTP
        );
        if (response.status === 'OK') {
            formActions.change('userProfile.otpCode', '');
            formActions.resetValidity('userProfile.otpCode');
            resetOtpStatus();
            dispatch({ type: OtpTransferCodeBoxActions.RETRY_OTP_CLICK });
            resetOtpValue();
            await onHandleSetOtpTransferData();
            recordTransferOtpEvent({
                path: TRANSFER_OTP_PATH,
                actionEvent: 'aviso nueva transferencia realizada',
                tagEvent: 'vista',
            });
        }

        setTimeout(() => {
            preLoaderActions.hidePreLoader();
        }, 500);
    };

    const recordTransferOtpEvent = ({ path, actionEvent, tagEvent }) => {
        const { encryptedDocNumber, recordId, userId, investmentType } = getUserProfileData({
            userProfile,
        });
        recordEvent(
            path,
            PAGE_PRODUCT,
            actionEvent,
            tagEvent,
            recordId,
            userId,
            RELIABLE[userProfile.identityMethod],
            investmentType,
            encryptedDocNumber,
            {
                categoriaEvento: path,
                accionEvento: actionEvent,
                etiquetaEvento: tagEvent,
                nombreProducto: DOCUMENTATION_PRODUCT_NAME,
            }
        );
    };

    const handleValidateOTPClick = async () => {
        preLoaderActions.showPreLoader();
        hideAlternativeOptionModal && hideAlternativeOptionModal();

        const response = await ibanActions.ibanTransferOTPCall(
            userProfile.accessToken,
            'POST',
            IBAN_TRANSFER_OTP_VALIDATE,
            { otp: otpValue }
        );

        const POSIBLE_OTP_STATUSES = {
            OK: () => {
                formActions.setValidity('userProfile.otpCode', { valid: true });
                resetOtpStatus();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_OTP_OK });
                recordTransferOtpEvent({
                    path: TRANSFER_OTP_PATH,
                    actionEvent: TRANSFER_OTP_PATH_TAG,
                    tagEvent: 'exito',
                });
                finishStep();
            },
            INCORRECT: () => {
                formActions.change('userProfile.otpCode', '');
                formActions.setValidity('userProfile.otpCode', { valid: false });
                resetOtpStatus();
                resetOtpValue();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_OTP_INCORRECT });
                recordEventError(
                    TRANSFER_OTP_PATH,
                    TRANSFER_OTP_PATH_TAG,
                    'error',
                    TRANSFER_OTP_PATH_TAG,
                    formatText('validation-otpFormatInvalid')
                );
            },
            INCORRECT_RETRY: () => {
                formActions.change('userProfile.otpCode', '');
                formActions.setErrors('userProfile.otpCode', { obsolete: true });
                resetOtpStatus();
                resetOtpValue();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_OTP_INCORRECT_RETRY });
                recordEventError(
                    TRANSFER_OTP_PATH,
                    TRANSFER_OTP_PATH_TAG,
                    'error',
                    TRANSFER_OTP_PATH_TAG,
                    formatText('validation-otpObsolete')
                );
            },
            INCORRECT_NO_RETRY: () => {
                formActions.change('userProfile.otpCode', '');
                formActions.setErrors('userProfile.otpCode', { maxTries: true });
                resetOtpStatus();
                resetOtpValue();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_OTP_INCORRECT_NO_RETRY });
                recordEventError(
                    TRANSFER_OTP_PATH,
                    TRANSFER_OTP_PATH_TAG,
                    'error',
                    TRANSFER_OTP_PATH_TAG,
                    formatText('validation-otpAllTriesMade')
                );
            },
            EXPIRED_RETRY: () => {
                formActions.change('userProfile.otpCode', '');
                formActions.setErrors('userProfile.otpCode', { obsolete: true });
                resetOtpStatus();
                resetOtpValue();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_EXPIRED_RETRY });
                recordEventError(
                    TRANSFER_OTP_PATH,
                    TRANSFER_OTP_PATH_TAG,
                    'error',
                    TRANSFER_OTP_PATH_TAG,
                    formatText('validation-otpObsolete')
                );
            },
            EXPIRED_NO_RETRY: () => {
                formActions.change('userProfile.otpCode', '');
                formActions.setErrors('userProfile.otpCode', { maxTries: true });
                resetOtpStatus();
                resetOtpValue();
                dispatch({ type: OtpTransferCodeBoxActions.VALIDATE_EXPIRED_NO_RETRY });
                recordEventError(
                    TRANSFER_OTP_PATH,
                    TRANSFER_OTP_PATH_TAG,
                    'error',
                    TRANSFER_OTP_PATH_TAG,
                    formatText('validation-otpAllTriesMade')
                );
            },
        };

        POSIBLE_OTP_STATUSES[response.status] ? POSIBLE_OTP_STATUSES[response.status]() : resetOtpStatus();

        setTimeout(() => {
            preLoaderActions.hidePreLoader();
        }, 500);
    };

    const onHandleClickMoreoptions = event => {
        showAlternativeOption(event);
        recordEventCustomOnly({
            accionEvento: 'otp-ibanid',
            etiquetaEvento: 'ver opciones',
            categoriaEvento: 'eventos IBAN OTP',
        });
    };

    const resetOtpStatus = () => {
        dispatch({ type: OtpTransferCodeBoxActions.RESET_OTP_STATUS });
    };
    const renderVerificationAlternativeContainer = () => {
        return (
            <MessageBox
                boxColor="yellow"
                iconColor="warning-2-center"
                titleText={formatText('otpCode-transferOTPCodeSubTitle')}
                contentText={formatText('otpCode-transferOTPCodewarningtext')}
                buttonText={!isOtpValidated && formatText('otpCode-transferOTPButton')}
                buttonAction={onHandleClickMoreoptions}
            />
        );
    };

    const renderCodeNotFoundVerificationAlternativeContainer = () => {
        const showHelpModalLinkComponent = () => (
            <Fragment>
                <p className="opt-transfer-modal-link" onClick={showHelpModal}>
                    <FormattedText value="otpCode-transferOTPCodewarninglink_TC" />
                </p>
                {formatText('otpCode-transferOTPCodewarningtext_TC')}
            </Fragment>
        );

        return <MessageBox boxColor="yellow" iconColor="warning-2-center" contentText={showHelpModalLinkComponent()} />;
    };

    const handleOtpOnFocusOut = isIncomplete => {
        if (isIncomplete) {
            dispatch({
                type: OtpTransferCodeBoxActions.CHANGE_ERROR_MESSAGE,
                payload: formatText('validation-otpFormatInvalid'),
            });

            return;
        }

        if (errorMessage) {
            return;
        }

        dispatch({
            type: OtpTransferCodeBoxActions.CHANGE_ERROR_MESSAGE,
            payload: '',
        });
    };

    const handleOtpOnFocusIn = () => {
        showAlternativeOption && handleOnFocusInput && handleOnFocusInput();
    };

    const handleValidateField = value => ONLY_ALPHANUMERIC_REGEX.test(value);

    return (
        <div className="otp-code">
            <ConditionalToggling
                condition={
                    isClickedRetryOTPChallenge &&
                    !retryTransfer &&
                    !expiredOtpRetry &&
                    !expiredOtpWithoutRetry &&
                    !isOtpValidated &&
                    !incorrectOtpRetry &&
                    !incorrectOtpWithoutRetry &&
                    !incorrectOtp
                }
            >
                <MessageBox
                    boxColor="green"
                    contentText={formatText('otpCode-retrySentOtpMsg')}
                    iconColor="success-green"
                />
            </ConditionalToggling>

            {displayCodeNotFound
                ? renderCodeNotFoundVerificationAlternativeContainer()
                : renderVerificationAlternativeContainer()}
            {}

            <div className="otp-code-container">
                <div className="otp-code-title">
                    <FormattedText value="otpCode-transferOTPCodeTitle" />
                </div>

                <div className="otp-transfer-input">
                    <div>
                        <SplitInput
                            value={otpValue}
                            howManyCharacters={OTP_CHARACTERS_LENGTH}
                            onFocusIn={handleOtpOnFocusIn}
                            onFocusOut={handleOtpOnFocusOut}
                            onChange={handleOnChangeSplitInput}
                            hasError={Boolean(errorMessage)}
                            showCharacters={true}
                            validateField={handleValidateField}
                            isCompleted={isOtpCompleted}
                        />
                        {errorMessage && <p className="error-message">{errorMessage}</p>}
                    </div>
                    <ConditionalToggling condition={showOtpButton}>
                        <div className="col-xs-12 col-sm-3 validate-container">
                            <button
                                className="form__button form__button-primary"
                                onClick={handleValidateOTPClick}
                                disabled={!isOtpCompleted}
                            >
                                {formatText('otpCode-otpValidateButton')}
                            </button>
                        </div>
                    </ConditionalToggling>

                    <ConditionalToggling condition={isOtpValidated}>
                        <div className="col-xs-12 col-sm-3 success-icon-hide">
                            <i className={`icons icons--icon-success-green-old`} />
                        </div>
                    </ConditionalToggling>
                </div>

                <ConditionalToggling
                    condition={
                        !isOtpValidated &&
                        !isClickedRetryOTPChallenge &&
                        (retryTransfer || incorrectOtpRetry || expiredOtpRetry)
                    }
                >
                    <div className="otp-code-retry">
                        <FormattedText value="otpCode-transferOTPCodeRetryText" />
                        <a onClick={handleRetryOTPClick}>{formatText('otpCode-otpRetryButton')}</a>
                    </div>
                </ConditionalToggling>
            </div>

            <OtpTransferMessages
                expiredOtpRetry={expiredOtpRetry}
                incorrectOtpRetry={incorrectOtpRetry}
                incorrectOtpWithoutRetry={incorrectOtpWithoutRetry}
                expiredOtpWithoutRetry={expiredOtpWithoutRetry}
            />
        </div>
    );
};

OtpTransferCodeBox.defaultProps = {
    retryTransfer: false,
    onHandleDisableNextButton: () => {},
    onHandleSetOtpTransferData: () => {},
};

OtpTransferCodeBox.propTypes = {
    retryTransfer: PropTypes.bool.isRequired,
    onHandleDisableNextButton: PropTypes.func.isRequired,
    onHandleSetOtpTransferData: PropTypes.func.isRequired,
    displayCodeNotFound: PropTypes.bool.isRequired,
    formActions: PropTypes.func.isRequired,
    userProfile: PropTypes.func.isRequired,
    ibanActions: PropTypes.func.isRequired,
    preLoaderActions: PropTypes.func.isRequired,
    showAlternativeOption: PropTypes.func.isRequired,
    showHelpModal: PropTypes.func.isRequired,
    handleOnFocusInput: PropTypes.func.isRequired,
    hideAlternativeOptionModal: PropTypes.func,
};

export default connect(
    state => ({
        userProfile: state.userProfile,
        userProfileForm: state.userProfileForm,
        immStepsState: state.steps,
    }),
    dispatch => ({
        ibanActions: bindActionCreators(ibanActions, dispatch),
        preLoaderActions: bindActionCreators(preLoaderActions, dispatch),
        formActions: bindActionCreators(actions, dispatch),
    })
)(OtpTransferCodeBox);
